import { styled } from "goober";
import type { ReactNode } from "react";
import { Text } from "../Text";
import {
  formatCacheHitRate,
  formatEstimatedCostUsd,
  formatTokenCount,
  type AiUsageSummary,
} from "./aiUsage";
import { MONITORING_PANEL_PADDING } from "./monitoringPanelLayout";

const Strip = styled("div")`
  padding: ${MONITORING_PANEL_PADDING}px;
  border-radius: 12px;
  background: var(--surface-secondary);
  border: 0.5px solid var(--border-alpha);
  container-type: inline-size;
`;

// The flex layout lives on Inner, not Strip: an element with container-type
// can't query its own size, only its descendants can — so Inner (a child of the
// query container) is what switches between the wide bar and the stacked card.
const Inner = styled("div")`
  display: flex;
  align-items: center;
  align-content: flex-start;
  justify-content: space-between;
  gap: 24px;
  flex-wrap: wrap;

  /* When the strip is squeezed into a narrow card (the responsive collapse),
     stack vertically: picker top-right, stats left-aligned down the column. */
  @container (max-width: 500px) {
    flex-direction: column;
    align-items: flex-start;
    flex-wrap: nowrap;
  }
`;

const Stats = styled("div")`
  display: flex;
  flex-wrap: wrap;
  gap: clamp(24px, 5cqi, 64px);
  align-items: flex-start;

  @container (max-width: 500px) {
    flex-direction: column;
    gap: 16px;
  }
`;

const PickerSlot = styled("div")`
  @container (max-width: 500px) {
    order: -1;
    align-self: stretch;

    /* fill the card width: the Dropdown opener and its pill button stretch, with
       the chevron pinned to the right. */
    & > * {
      width: 100%;
    }
    & button {
      width: 100%;
      max-width: none;
      justify-content: space-between;
    }
  }
`;

export const StatCell = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

type Props = { summary: AiUsageSummary; picker?: ReactNode };

export function AiOverviewStats({ summary, picker }: Props) {
  const stats = [
    {
      label: "Estimated spend",
      value: formatEstimatedCostUsd(summary.estimatedCostUsd ?? 0),
    },
    { label: "Tokens", value: formatTokenCount(summary.totalTokens) },
    { label: "Turns", value: summary.requestCount.toLocaleString("en-US") },
    {
      label: "Cache hit rate",
      value:
        summary.inputTokens === 0
          ? "0%"
          : formatCacheHitRate(summary.cacheReadTokens, summary.inputTokens),
    },
  ];

  return (
    <Strip>
      <Inner>
        <Stats>
          {stats.map((stat) => (
            <StatCell key={stat.label}>
              <Text variant="text-small" color="var(--text-secondary)">
                {stat.label}
              </Text>
              <Text variant="display-md">{stat.value}</Text>
            </StatCell>
          ))}
        </Stats>
        {picker ? <PickerSlot>{picker}</PickerSlot> : null}
      </Inner>
    </Strip>
  );
}
